import type { Timeline } from "@hoee/schema";

export type ScaleName = "poster" | "true";

/**
 * A way of laying time down the chart. `u` runs from 0 at the axis start to 1 at its end, so the two
 * scales can be blended: a point drawn at mix(poster.toU(y), true.toU(y), k) slides smoothly between them.
 */
export interface TimeScale {
  readonly name: ScaleName;
  toU(year: number): number;
  fromU(u: number): number;
  /** How much of `u` one year takes up at this year. */
  slope(year: number): number;
}

interface Piece {
  from: number;
  to: number;
  u0: number;
  u1: number;
}

function piecewise(name: ScaleName, ranges: { from: number; to: number; weight: number }[]): TimeScale {
  const total = ranges.reduce((n, r) => n + (r.to - r.from) * r.weight, 0);
  let u = 0;
  const pieces: Piece[] = ranges.map((r) => {
    const u0 = u;
    u += ((r.to - r.from) * r.weight) / total;
    return { from: r.from, to: r.to, u0, u1: u };
  });
  const last = pieces[pieces.length - 1] as Piece;
  const pieceOfYear = (y: number) => pieces.find((p) => y < p.to) ?? last;
  const pieceOfU = (v: number) => pieces.find((p) => v < p.u1) ?? last;
  return {
    name,
    // Outside the axis the end pieces carry on in a straight line, so things before 3000 BC still have a place.
    toU(year) {
      const p = pieceOfYear(year);
      return p.u0 + ((year - p.from) / (p.to - p.from)) * (p.u1 - p.u0);
    },
    fromU(v) {
      const p = pieceOfU(v);
      return p.from + ((v - p.u0) / (p.u1 - p.u0)) * (p.to - p.from);
    },
    slope(year) {
      const p = pieceOfYear(year);
      return (p.u1 - p.u0) / (p.to - p.from);
    },
  };
}

/** The poster's scale (BC compressed, as printed) and true time (every year the same height). */
export function timeScales(axis: Timeline["axis"]): Record<ScaleName, TimeScale> {
  const poster = piecewise(
    "poster",
    axis.scales.map((s) => ({ from: s.from, to: s.to, weight: 1 / s.yearsPerTick })),
  );
  const linear = piecewise("true", [{ from: axis.start, to: axis.end, weight: 1 }]);
  return { poster, true: linear };
}
